/**
 * Reader-study onboarding state atoms.
 *
 * Persisted in sessionStorage (via Jotai's atomWithStorage from jotai/utils)
 * so a page reload during the practice round doesn't restart from scratch,
 * but a new browser session always starts fresh. The study dataset is
 * per-session.
 *
 * Partition:
 *  onboardingPhase = "practice" | "study"
 *  practiceCompleted: number of practice cases signed (0‒N)
 *
 * The former "onboarding" phase (auto-showing 5-step walkthrough) has been
 * removed. The instructions are now available on-demand via the Instructions
 * button on the StudyLockScreen. Sessions start directly in the practice
 * round.
 */
import { atom } from "jotai";
import { atomWithStorage, createJSONStorage } from "jotai/utils";

export type OnboardingPhase = "practice" | "study";

const VALID_PHASES: readonly OnboardingPhase[] = ["practice", "study"];

/**
 * sessionStorage-backed storage for the phase atom that coerces any
 * unrecognized persisted value (e.g. the removed "onboarding" phase, or a
 * future value an older build doesn't know) back to "practice". Without this a
 * stale value would escape the union and either render nothing or pre-open the
 * study gate, skipping the practice round.
 */
function createPhaseStorage() {
  const base = createJSONStorage<OnboardingPhase>(() => sessionStorage);
  return {
    ...base,
    getItem(key: string, initialValue: OnboardingPhase): OnboardingPhase {
      const stored = base.getItem(key, initialValue);
      // sessionStorage is synchronous, so stored is never a Promise here.
      return VALID_PHASES.includes(stored as OnboardingPhase)
        ? (stored as OnboardingPhase)
        : "practice";
    },
  };
}

/** Which phase the reader is currently in. Starts at "practice" — the
 *  removed "onboarding" walkthrough no longer gates entry. Persisted in
 *  sessionStorage so a reload mid-practice resumes, but a new browser session
 *  starts fresh (the study dataset is per-session). */
export const onboardingPhaseAtom = atomWithStorage<OnboardingPhase>(
  "reader-study-onboarding-phase",
  "practice",
  createPhaseStorage(),
  { getOnInit: true },
);

/**
 * Number of practice cases the reader has signed. Gate to the study set
 * is: practiceSignedCount >= PRACTICE_CASE_MIN (4).
 * Session-level counter — sessionStorage means practice rounds can't
 * accumulate across browser sessions.
 */
export const practiceSignedCountAtom = atomWithStorage<number>(
  "reader-study-practice-signed-count",
  0,
  createJSONStorage(() => sessionStorage),
  { getOnInit: true },
);

/** Minimum number of practice cases before "Begin Study" unlocks. */
export const PRACTICE_CASE_MIN = 4;

/** Derived: is the begin-study gate open? */
export const canBeginStudyAtom = atom((get) => get(practiceSignedCountAtom) >= PRACTICE_CASE_MIN);
